<template>
  <div id="map-final-container" style="height: 60vh">
    <Popup />

    <ControlFinal />

    <LegendFinal />
  </div>
</template>

<script>
import * as d3 from 'd3';
import { mapGetters } from 'vuex';

import mapServices from '@/services/map';

// components
import Popup from '@/components/ui/map/Popup.vue';
import ControlFinal from '@/components/ui/map/ControlFinal.vue';
import LegendFinal from '@/components/ui/map/LegendFinal.vue';

export default {
  name: 'MapFinal',
  components: {
    Popup,
    ControlFinal,
    LegendFinal,
  },
  computed: {
    ...mapGetters('viewFinal', ['zoom', 'railroads', 'oilpipelines', 'gaspipelines', 'ports']),
  },
  watch: {
    zoom(newVal) {
      d3.select('#map-final-container .mapboxgl-custom-popup').style('display', 'none');

      // zoom map
      this.map.zoomTo(newVal, 2);
    },
    railroads(newVal) {
      this.handleLayerVisibility('railroads', newVal);
    },
    oilpipelines(newVal) {
      this.handleLayerVisibility('oilpipelines', newVal);
    },
    gaspipelines(newVal) {
      this.handleLayerVisibility('gaspipelines', newVal);
    },
    ports(newVal) {
      this.handleLayerVisibility('ports', newVal);
    },
  },
  data() {
    return {
      map: null,
      defaultOptions: {
        container: 'map-final-container',
        style: process.env.VUE_APP_MAPBOX_STYLE_TERRAIN,
        center: [66.58, 33.49],
        zoom: 2.41,
        minZoom: 2.41,
        maxZoom: 7,
      },
    };
  },
  mounted() {
    // init Mapbox
    this.map = mapServices.createMap(this.defaultOptions);

    this.map.on('load', () => {
      // add corridors layers
      this.loadFinalLayers();

      // map events
      this.loadFinalEvents();
    });
  },
  methods: {
    loadFinalLayers() {
      // add geojson sources
      this.map.addSource('brirailroads', {
        type: 'geojson',
        data: '/data/map/final/brirailroads.json',
      });
      this.map.addSource('brirailstops', {
        type: 'geojson',
        data: '/data/map/final/brirailstops.json',
      });
      this.map.addSource('oilpipelines', {
        type: 'geojson',
        data: '/data/map/final/oilpipelines.json',
      });
      this.map.addSource('gaspipelines', {
        type: 'geojson',
        data: '/data/map/final/gaspipelines.json',
      });
      this.map.addSource('ports', {
        type: 'geojson',
        data: '/data/map/final/ports.json',
      });

      const layers = [
        {
          id: 'brirailroads',
          type: 'line',
          source: 'brirailroads',
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#ff2424',
            'line-width': 2,
          },
        },
        {
          id: 'brirailstops',
          type: 'circle',
          source: 'brirailstops',
          paint: {
            'circle-radius': 3,
            'circle-color': '#fff',
            'circle-stroke-color': '#ff2424',
            'circle-stroke-width': 1.5,
          },
        },
        {
          id: 'oilpipelines',
          type: 'line',
          source: 'oilpipelines',
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#58618d',
            'line-width': 5,
          },
        },
        {
          id: 'gaspipelines',
          type: 'line',
          source: 'gaspipelines',
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#949db8',
            'line-width': 5,
          },
        },
        {
          id: 'ports',
          type: 'circle',
          source: 'ports',
          paint: {
            'circle-radius': ['interpolate', ['exponential', 0.5], ['zoom'], 1, 2, 2.99, 4, 3, 5, 22, 5],
            'circle-color': '#479a89',
            'circle-stroke-color': '#479a89',
            'circle-stroke-width': 1,
            'circle-stroke-opacity': 1,
          },
        },
      ];

      // add all layers
      layers.forEach(layerOption => {
        this.map.addLayer(layerOption);
      });
    },
    loadFinalEvents() {
      this.map.dragPan.enable();
      this.map.scrollZoom.enable();

      this.map.on('click', e => {
        let isPopup =
          d3.select('#map-final-container .mapboxgl-custom-popup').style('display') === 'block' ? true : false;

        if (isPopup) {
          // hide popup
          d3.select('#map-final-container .mapboxgl-custom-popup').style('display', 'none');
        }
      });

      // add click and hover events to all layers
      this.map.on('click', 'brirailroads', this.handleMapPopup);
      this.map.on('click', 'brirailstops', this.handleMapPopup);
      this.map.on('click', 'oilpipelines', this.handleMapPopup);
      this.map.on('click', 'gaspipelines', this.handleMapPopup);
      this.map.on('click', 'ports', this.handleMapPopup);
    },
    handleLayerVisibility(layerId, visible) {
      const visibility = visible ? 'visible' : 'none';

      if (layerId === 'railroads') {
        this.map.setLayoutProperty('brirailroads', 'visibility', visibility);
        this.map.setLayoutProperty('brirailstops', 'visibility', visibility);
      } else {
        this.map.setLayoutProperty(layerId, 'visibility', visibility);
      }
    },
    handleMapPopup(e) {
      // select popup element
      const popupEl = d3.select('#map-final-container .mapboxgl-custom-popup');

      // popup content
      let title = e.features[0].properties.Name;

      let category = e.features[0].properties.Subcategory === 'null' ? '' : `${e.features[0].properties.Subcategory}`;

      let description =
        e.features[0].properties.CostUSbillions === 'null'
          ? ''
          : `Cost: US$ ${e.features[0].properties.CostUSbillions} Billion`;
      let description2 = e.features[0].properties.Status === 'null' ? '' : `Status: ${e.features[0].properties.Status}`;

      switch (e.features[0].layer.id) {
        case 'ports':
          category = e.features[0].properties.Subcategory;
          break;

        default:
          break;
      }

      // update popup el
      popupEl.style('display', 'block');

      // update popup content
      this.$store.dispatch('popup/updateTitle', title);
      this.$store.dispatch('popup/updateCategory', category);
      this.$store.dispatch('popup/updateDescription', description);
      this.$store.dispatch('popup/updateDescription2', description2);
    },
  },
};
</script>

<style lang="stylus" scoped>
</style>
